/**
 * TruthSparkline — 7-day at-a-glance health strip for the P&L Truth Contract.
 *
 * Why this exists:
 *   The truth contract gates every metric on the page. Operators need to see
 *   at a glance whether the system has been delivering trustworthy answers
 *   over the past week — not just right now. This is the "trust surface".
 *
 * Data source: GET /api/accounts/pnl/truth-summary?outlet_id&days=7
 *   daily: [{ date, truth_state, blocking_reasons }, ...]
 *
 * Rendering:
 *   Each day = one circle.
 *     🟢 emerald  — VALID
 *     🟠 amber    — DIRECTIONAL
 *     🔴 rose     — NOT_ACTIONABLE | INVALID
 *     ⚪ zinc     — NO_DATA (outlet had no /pnl/realtime call that day)
 *
 *   Hovering a dot reveals the date + state + first blocking reason.
 *   Renders nothing until data has loaded successfully.
 */
import React, { useEffect, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATE_TONE = {
  VALID:           { dot: 'bg-emerald-400',  ring: 'ring-emerald-400/30',  label: 'Valid' },
  DIRECTIONAL:     { dot: 'bg-amber-400',    ring: 'ring-amber-400/30',    label: 'Directional' },
  NOT_ACTIONABLE:  { dot: 'bg-rose-400',     ring: 'ring-rose-400/30',     label: 'Not actionable' },
  INVALID:         { dot: 'bg-rose-500',     ring: 'ring-rose-500/30',     label: 'Invalid' },
  NO_DATA:         { dot: 'bg-zinc-700',     ring: 'ring-zinc-700/30',     label: 'No snapshot' },
};

const formatShortDate = (iso) => {
  // 2026-04-22 → "Apr 22"
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return iso;
  }
};

const TruthSparkline = () => {
  const { user } = useAuth();
  const outletId = user?.outlet_id;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!outletId) return;
    (async () => {
      try {
        const res = await accountsAPI.getTruthSummary(outletId, 7);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e?.message || 'load_failed');
      }
    })();
    return () => { alive = false; };
  }, [outletId]);

  if (error || !data || !Array.isArray(data.daily) || data.daily.length === 0) {
    return null;
  }

  const counts = data.state_counts || {};
  const validCount = counts.VALID || 0;
  const total = data.total_snapshots || 0;
  const headline =
    total === 0
      ? 'No truth history yet — once you load a P&L, daily snapshots will appear here.'
      : `${validCount}/${total} day${total === 1 ? '' : 's'} fully trustworthy`;

  return (
    <div
      className="px-4 py-2.5 rounded-lg border border-zinc-800/70 bg-zinc-900/40"
      data-testid="truth-sparkline"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">
            7-day truth
          </span>
          <span className="text-xs text-zinc-300">{headline}</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="truth-sparkline-dots">
          {data.daily.map((d) => {
            const tone = STATE_TONE[d.truth_state] || STATE_TONE.NO_DATA;
            const reason = (d.blocking_reasons && d.blocking_reasons[0]) || null;
            const title =
              `${formatShortDate(d.date)} — ${tone.label}` +
              (reason ? `\n${reason}` : '');
            return (
              <span
                key={d.date}
                title={title}
                aria-label={title}
                data-testid={`truth-dot-${d.date}`}
                data-truth-state={d.truth_state}
                className={`w-2.5 h-2.5 rounded-full ${tone.dot} ring-2 ${tone.ring} cursor-default transition-transform hover:scale-125`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TruthSparkline;
